import React from 'react';

// Bottom-navigation icon set. Each icon is drawn on a 24px grid with rounded 2px strokes; when `active` the
// main shape picks up a soft tinted fill and its small details go solid, so the selected tab reads at a glance.
export interface NavIconProps extends React.SVGProps<SVGSVGElement> {
  active?: boolean;
}

const Base: React.FC<NavIconProps & { children: (fill: string, tint: number) => React.ReactNode }> = ({ active, children, ...props }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" {...props}>
    {children(active ? 'currentColor' : 'none', active ? 0.24 : 0)}
  </svg>
);

export const NavHomeIcon: React.FC<NavIconProps> = (p) => (
  <Base {...p}>{(fill, tint) => (<>
    <path d="M4 10.8 12 4l8 6.8V19a1.5 1.5 0 0 1-1.5 1.5H15V15a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v5.5H5.5A1.5 1.5 0 0 1 4 19z" fill={fill} fillOpacity={tint} />
  </>)}</Base>
);

export const NavZipsIcon: React.FC<NavIconProps> = (p) => (
  <Base {...p}>{(fill, tint) => (
    <path d="M13.2 2.8 5 13.4h5.8l-1 7.8 8.2-10.6h-5.8z" fill={fill} fillOpacity={tint} />
  )}</Base>
);

export const NavDiscoverIcon: React.FC<NavIconProps> = (p) => (
  <Base {...p}>{(fill, tint) => (<>
    <circle cx="12" cy="12" r="9" fill={fill} fillOpacity={tint} />
    <path d="m15.6 8.4-1.9 5.3-5.3 1.9 1.9-5.3z" fill={p.active ? 'currentColor' : 'none'} />
  </>)}</Base>
);

export const NavSpotsIcon: React.FC<NavIconProps> = (p) => (
  <Base {...p}>{(fill, tint) => (<>
    <path d="M12 21s-6.5-5.5-6.5-11a6.5 6.5 0 0 1 13 0c0 5.5-6.5 11-6.5 11z" fill={fill} fillOpacity={tint} />
    <circle cx="12" cy="10" r="2.3" fill={p.active ? 'currentColor' : 'none'} />
  </>)}</Base>
);

// Buy & Sell: two arrows, one pointing right and the other pointing the opposite way (swap / exchange).
export const NavMarketIcon: React.FC<NavIconProps> = (p) => (
  <Base {...p}>{(fill) => (<>
    <path d="M4 8h15" />
    <path d="m15 4 4 4-4 4" fill={fill} />
    <path d="M20 16H5" />
    <path d="m9 12-4 4 4 4" fill={fill} />
  </>)}</Base>
);

export const NavMessagesIcon: React.FC<NavIconProps> = (p) => (
  <Base {...p}>{(fill, tint) => (<>
    <path d="M4 6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5v7a2.5 2.5 0 0 1-2.5 2.5H11l-4 3.8V16h0A2.5 2.5 0 0 1 4 13.5z" fill={fill} fillOpacity={tint} />
    <path d="M8.5 9.5h7M8.5 12.2h4" />
  </>)}</Base>
);

export const NavProfileIcon: React.FC<NavIconProps> = (p) => (
  <Base {...p}>{(fill, tint) => (<>
    <circle cx="12" cy="8" r="3.7" fill={fill} fillOpacity={tint} />
    <path d="M4.6 20.2c.6-3.6 3.6-5.7 7.4-5.7s6.8 2.1 7.4 5.7" fill={fill} fillOpacity={tint} />
  </>)}</Base>
);

export const NAV_ICONS: Record<string, React.FC<NavIconProps>> = {
  feed: NavHomeIcon,
  zips: NavZipsIcon,
  discover: NavDiscoverIcon,
  'spot-map': NavSpotsIcon,
  marketplace: NavMarketIcon,
  messenger: NavMessagesIcon,
  profile: NavProfileIcon,
};
